import { TaxRate } from '@principle-theorem/accounting';
import {
  IAppointment,
  IInvoice,
  IPatient,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  Timestamp,
  WithRef,
  addDoc,
  snapshot,
  toNamedDocument,
} from '@principle-theorem/shared';
import { Appointment } from '../appointment/appointment';
import { TreatmentStep } from '../clinical-charting/treatment/treatment-step';
import { stafferToNamedDoc } from '../common';
import { OrganisationCache } from '../organisation/organisation-cache';
import { Patient } from '../patient/patient';
import { toAccountDetails } from './account-details';
import { BillingEntity } from './billing-entity';
import { treatmentStepToLineItems } from './custom-line-items';
import { HealthcareClaimGenerator } from './healthcare-claim-operators';
import { Invoice } from './invoice';
import { Practice } from '../practice/practice';

export async function buildInvoiceForAppointment(
  appointment: WithRef<IAppointment>,
  patientRef: DocumentReference<IPatient>,
  taxRate: TaxRate,
  createdAt?: Timestamp
): Promise<IInvoice> {
  if (!appointment.event) {
    throw new Error('Missing required data');
  }

  const practice = await OrganisationCache.practices.getDoc(
    appointment.event.practice.ref
  );
  const patient = await OrganisationCache.patients.getDoc(patientRef);
  const primaryContact = await Patient.resolvePrimaryContact(patient);

  const treatmentPlan = await Appointment.treatmentPlan(appointment);
  const practitioner = await OrganisationCache.staff.get.getDoc(
    appointment.practitioner.ref
  );

  const treatmentStep = await snapshot(
    TreatmentStep.fromAppointment$(appointment)
  );

  const items = treatmentStep
    ? treatmentStepToLineItems(
        treatmentStep,
        treatmentPlan,
        stafferToNamedDoc(practitioner),
        taxRate
      )
    : [];

  const claims = await HealthcareClaimGenerator.createClaims(
    items,
    practitioner,
    practice.ref
  );

  const invoice = Invoice.init({
    from: toAccountDetails(practice),
    to: primaryContact
      ? toAccountDetails({ patient, primaryContact })
      : toAccountDetails(patient),
    practice: toNamedDocument(practice),
    items,
    claims,
    createdAt,
    includeDepositsInAllocations:
      practice.settings?.accounting?.includeDepositsInAllocations ?? false,
  });

  const brand = await OrganisationCache.brands.getDoc(
    Practice.brandRef(practice)
  );
  if (!brand.settings.billing?.enableBillingEntitySelection) {
    return invoice;
  }

  const billingEntity = await BillingEntity.getBillingEntityForInvoice(invoice);
  if (!billingEntity) {
    return invoice;
  }

  return {
    ...invoice,
    from: billingEntity.from,
    attributedTo: billingEntity.attributedTo,
  };
}

export async function createInvoiceForAppointment(
  appointmentRef: DocumentReference<IAppointment>,
  taxRate: TaxRate
): Promise<DocumentReference<IInvoice>> {
  const appointment = await Firestore.getDoc(appointmentRef);
  const patientRef = Appointment.patientRef(appointment);
  const invoice = await buildInvoiceForAppointment(
    appointment,
    patientRef,
    taxRate
  );
  const ref = await addDoc(Patient.invoiceCol({ ref: patientRef }), invoice);

  appointment.invoiceRef = ref;
  await Firestore.saveDoc(appointment);
  return ref;
}
